import React, { useEffect } from "react";
import { Route, useHistory, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";

// Config
import { statementRequestSources, statementStatus } from "src/config/values";
import { getRoute, urls } from "src/config/routes";
import { useAppInsights } from "src/utils/telemetry/AppInsights";
import styles from "src/config/styles";
import colors from "src/config/colors";

// Actions
import { getStatements, clearMediaError } from "src/state/statements/actions";

// Assets
import pdfIcon from "src/assets/icons/pdf_ico.svg";

// Components
import ContentCardHeader from "../ContentCardHeader";
import GalleryHandler from "../GalleryHandler";
import LoadingSection from "../LoadingSection";
import PendingStatementItem from "../PendingStatementItem";
import Text from "../Text";
import StatementItem from "../StatementItem";

const useStyles = makeStyles(theme => ({
  root: {
    border: styles.values.border
  },
  buttonWrapper: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1)
  },
  content: {
    padding: theme.spacing(5),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap"
  },
  placeholderContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(10, 0),
    gap: theme.spacing(2),
    margin: "0 auto"
  },
  placeholderText: {
    textAlign: "center",
    color: colors.riverBed,
    opacity: "0.4",
    marginBottom: theme.spacing(1),
    lineHeight: 1.5
  }
}));

const CaseStatementsPanel = ({ isInterview }) => {
  const { caseId } = useParams();
  const { currentCase, data, isLoading, error } = useSelector(
    state => state.statements
  );
  const { trackException } = useAppInsights();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const rawStatements =
    data?.map(d => ({ ...d, filename: `${d.id}.pdf` })) ?? [];

  const statements = isInterview
    ? rawStatements
    : rawStatements.filter(
        d => d.requestSource !== statementRequestSources.SELF_ADMINISTERED
      );

  useEffect(() => {
    return () => {
      dispatch(clearMediaError());
    };
  }, [dispatch]);

  useEffect(() => {
    if ((currentCase !== caseId || !data) && !error && !isLoading) {
      dispatch(
        getStatements({
          requestSource:
            isInterview && statementRequestSources.SELF_ADMINISTERED,
          caseId,
          onFail: () =>
            trackException(`Failed to get statements from case: ${caseId}`)
        })
      );
    }
  }, [caseId, currentCase, data, dispatch, error, isLoading, trackException]);

  if (isLoading) {
    return (
      <Paper elevation={0} className={classes.root}>
        <LoadingSection normalHeight />
      </Paper>
    );
  }

  const handleClick = itemId => () => {
    const url = isInterview ? urls.saiDetail : urls.caseStatementDetail;
    history.push(getRoute(url, { caseId, itemId }));
  };

  const handleClickPending = itemId => () => {
    const url = isInterview ? urls.saiForm : urls.statement;
    history.push(getRoute(url, { caseId, itemId }));
  };

  const galleryHandlerProps = isInterview
    ? {
        defaultRoute: urls.sai,
        detailRoute: urls.saiDetail
      }
    : {
        defaultRoute: urls.statements,
        detailRoute: urls.caseStatementDetail
      };

  const filterStatements = s => s.state === statementStatus.COMPLETED;

  const onOpenPendingStatement = item => handleClickPending(item.id);

  return (
    <>
      <Paper elevation={0} className={classes.root}>
        <ContentCardHeader>
          <Text
            text={
              isInterview
                ? "selfAdministeredInterviewsPage.title"
                : "statementsPage.title"
            }
            type="heading2"
          />
        </ContentCardHeader>
        <div className={classes.content}>
          {statements && statements.length > 0 ? (
            statements.map(item =>
              // A statement moves Pending -> Draft as soon as the owner autosaves
              // any progress (see DraftAutosave); it's still not submitted, so it
              // belongs in the same "needs completing" bucket as Pending.
              [statementStatus.PENDING, statementStatus.DRAFT].includes(
                item.state
              ) ? (
                <PendingStatementItem
                  key={item.id}
                  statement={item}
                  onClick={handleClickPending(item.id)}
                />
              ) : (
                <StatementItem
                  key={item.id}
                  statement={item}
                  onClick={handleClick(item.id)}
                />
              )
            )
          ) : (
            <div className={classes.placeholderContainer}>
              <img width={80} src={pdfIcon} alt="" />
              <Text
                className={classes.placeholderText}
                text="common.units.statements.zero"
                type="subtitle1Big"
              />
            </div>
          )}
        </div>
      </Paper>
      <Route
        path={isInterview ? urls.saiDetail : urls.caseStatementDetail}
        exact
        render={renderProps =>
          statements && (
            <GalleryHandler
              {...renderProps}
              {...galleryHandlerProps}
              data={statements}
              view="statements"
              withFilter={filterStatements}
              onOutOfFilter={onOpenPendingStatement}
            />
          )
        }
      />
    </>
  );
};

CaseStatementsPanel.propTypes = {
  isInterview: PropTypes.bool
};

CaseStatementsPanel.defaultProps = {
  isInterview: false
};

export default CaseStatementsPanel;
